/**
 * RD-NAV-001 — feature-based routing shell (`/app/:feature`).
 *
 * Single source of truth for which roles may NAVIGATE to which feature. This is
 * a UX / navigation gate ONLY — it decides which screen renders and which nav
 * entries show. It is NOT the security boundary: every privileged read/write
 * stays enforced by RLS + SECURITY DEFINER RPCs. Each cell here is == or
 * STRICTER than what the server permits, so the guard never surfaces a screen
 * whose data the DB would deny.
 *
 * Canonical feature vocabulary resolves the cross-role naming collisions:
 *   - `route`  = the daily route board (admin internal key 'route';
 *                worker/supervisor internal key 'jobs').
 *   - `jobs`   = the jobs list / history (admin + billing).
 *   - `schedule` (admin internal key 'team-schedule').
 *   - `team`   = employee management (admin internal key 'employees').
 * See featureTabMap.js for the internal<->canonical plumbing.
 */

// All canonical features the /app shell knows about. RD-NAV-002: `activity`
// (worker/supervisor's route-board activity feed) is no longer a top-level
// feature — it's a sub-view of `route` (see subViewRoutes.js 'app/route' +
// featureTabMap.js's worker/supervisor `activity` internal key). A stale
// /app/activity bookmark redirects via legacyUrlRedirects.js.
export const CANONICAL_FEATURES = [
  'dashboard',
  'route',
  'jobs',
  'schedule',
  'customers',
  'team',
  'catalog',
  'invoices',
  'estimates',
  'reports',
  'communications',
  'history',
  'company',
  'account',
  'ai-hub',
];

/**
 * Legacy URL slugs → their current canonical feature. AppShell redirects these
 * (replace) so old bookmarks / saved last-routes keep landing on the renamed
 * tab; lastRouteStorage resolves them before its access check. Internal
 * activeTab keys are NOT affected (admin's internal 'branding' key stays —
 * see featureTabMap.js).
 *
 * `organization` is itself a retired slug now (renamed 2026-08-14 from
 * `branding`, then renamed again 2026-09-24 to `company` — the on-screen tab
 * is "Empresa / Company"), so BOTH `branding` and `organization` alias
 * straight to `company` (no chained lookups; each old slug resolves in one
 * hop).
 *
 * RD-NAV-002: `activity` is included too — worker/supervisor's route-board
 * activity feed was a top-level feature before it became a sub-view of
 * `route` (see CANONICAL_FEATURES note below); a saved/bookmarked
 * `/app/activity` resolves through this alias for the ACCESS check
 * (lastRouteStorage), and AppShell's own resolveLegacyAppUrl separately maps
 * the full path to `/app/route/activity`.
 */
export const LEGACY_FEATURE_ALIASES = {
  branding: 'company', // renamed 2026-08-14 branding->organization, 2026-09-24 organization->company
  organization: 'company', // renamed 2026-09-24 (URL said "organization", tab says Empresa/Company)
  activity: 'route',
};

/**
 * Safe lookup into LEGACY_FEATURE_ALIASES for a key that came off the URL
 * (a route param, a saved localStorage path segment). A bare
 * `LEGACY_FEATURE_ALIASES[feature]` lookup is exploitable: `feature` values
 * like `constructor` or `__proto__` resolve an INHERITED Object.prototype
 * member (a function / the prototype object itself — always truthy) instead
 * of `undefined`, which downstream code (AppShell's `if (aliased)` redirect)
 * would then navigate to as a literal path segment. `hasOwnProperty` scopes
 * the lookup to the object's own keys, so any prototype-chain name returns
 * null exactly like any other unknown feature.
 */
export function resolveLegacyFeatureAlias(feature) {
  return Object.prototype.hasOwnProperty.call(LEGACY_FEATURE_ALIASES, feature)
    ? LEGACY_FEATURE_ALIASES[feature]
    : null;
}

// role -> features that role may navigate to. Verified against the current
// per-role nav inventory and RLS (2026-05-30). `ai-hub` is additionally gated
// by aiEnabled at the call site; the route board's pool-lifecycle sub-views
// (`/app/route/season/<state>`) are additionally gated on pool industry —
// see subViewRoutes.js 'app/route' (path segments now, not `?pool=`).
export const FEATURE_ACCESS = {
  dashboard:      ['admin', 'supervisor', 'worker', 'billing', 'operations'],
  route:          ['admin', 'supervisor', 'worker', 'operations'],
  jobs:           ['admin', 'billing', 'operations'],
  schedule:       ['admin', 'supervisor', 'worker', 'operations'],
  customers:      ['admin', 'billing', 'operations'],
  team:           ['admin'],
  catalog:        ['admin'],
  invoices:       ['admin', 'billing'],
  estimates:      ['admin', 'billing'],
  reports:        ['admin', 'billing'],
  // Admin reaches the audit feed + email log via the History hub (feature
  // 'history'), NOT a top-level activity/communications feature — so a stale
  // /app/communications bookmark for an admin redirects cleanly to dashboard
  // instead of rendering dashboard content at a wrong URL. Worker/supervisor's
  // activity feed is a sub-view of `route` now (see CANONICAL_FEATURES note
  // above); billing keeps its own 'communications' feature.
  // Operations is excluded from all of: communications, history,
  // company, catalog, reports, estimates, invoices, team, ai-hub.
  communications: ['billing'],
  history:        ['admin'],
  company:        ['admin'],
  account:        ['admin', 'supervisor', 'worker', 'billing', 'operations'],
  // Worker's AI surface is the floating panel, not a hub tab — only admin /
  // supervisor / billing render AiHubTab. Operations excluded: no AI gatherer yet.
  'ai-hub':       ['admin', 'supervisor', 'billing'],
};

/**
 * Can `role` navigate to `feature`? Navigation gate only — see file header.
 */
export function canAccessFeature(role, feature) {
  const allowed = FEATURE_ACCESS[feature];
  return Array.isArray(allowed) && allowed.includes(role);
}

/**
 * Default landing feature per role. Preserves today's behavior — every role
 * lands on `dashboard`. (Worker-`route`-first is a deliberate later UX call.)
 */
export function defaultFeatureForRole(role) {
  switch (role) {
    case 'admin':
    case 'supervisor':
    case 'worker':
    case 'billing':
    case 'operations':
      return 'dashboard';
    default:
      return null;
  }
}
